// frontend/src/auth/LegalReacceptGate.tsx
// Hard-Gate, das eingeloggte User blockiert, bis sie geaenderte Rechtstexte
// (AGB / Datenschutz / Nutzungsbedingungen) neu akzeptiert haben. Der Server
// entscheidet, ob ein Versions-Bump vorliegt — wir reagieren nur auf das
// Ergebnis und setzen den Status nach Akzept zurueck.
//
// 74-misc Befund c (Julius): „Frag mich in 7 Tagen" (Postpone) hat nie
// tatsaechlich etwas verschoben — nach Ablauf der Karenz landete der Nutzer
// ohnehin im selben Hard-Gate, nur mit einer Drohkulisse (Countdown +
// Warnhinweis) davor. Diese Illusion ist jetzt weg: outdated.length > 0
// zeigt eine gesperrte Fläche mit aktiver Zustimmung. Registrierte Konten
// können sich alternativ abmelden; Gäste behalten ihre Sitzung und schließen
// bei Ablehnung die App, damit ihr nicht wiederherstellbares Konto erhalten bleibt.
// L1F: Der Server liefert die maßgeblichen Dialogtexte; lokale Overrides
// dürfen keine frühere Einwilligung in eine neue Rechtsversion hineintragen.
//
// Server bleibt kompatibel: POST /api/auth/postpone-legal existiert unveraen-
// dert weiter (server/legal.js/auth.js) fuer Alt-Clients vor diesem OTA-
// Bundle — dieser Client ruft ihn nur nicht mehr auf. must_act/days_remaining
// im Status bleiben im Server-Vertrag, dieser Client liest sie nicht mehr.
//
// Bewusst KEIN window.location.reload(): wir bleiben im SPA, refresh() holt
// frische Auth-/User-Daten, dann verschwindet das Gate.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../api/client';
import { acceptPendingLegal, getLegalStatus } from '../api/legal';
import type { LegalDocument, LegalStatusResponse } from '../api/types';
import { Button } from '../components/ui/Button';
import { LegalDialog, type LegalDocId } from '../components/ui/LegalDialog';
import { useT } from '../i18n';
import { LEGAL_SLUG_BY_DOCUMENT_KEY } from '../legal/legalSlugs';
import { useAuth } from './AuthProvider';
import { onLegalAcceptanceRequired } from './legalGateSignal';
import { onSessionExpired } from './sessionEvents';
import styles from './LegalReacceptGate.module.css';

export interface LegalReacceptGateProps {
  children: ReactNode;
  onGateStateChange?: (state: LegalReacceptGateState) => void;
}

export type LegalReacceptGateState = 'checking' | 'blocking' | 'clear';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; status: LegalStatusResponse }
  | { kind: 'session_expired' }
  | { kind: 'error' };

const LEGAL_REFETCH_MIN_INTERVAL_MS = 10_000;

function isUnauthorizedError(error: unknown): boolean {
  return error instanceof ApiError
    && (error.code === 'session_expired' || error.status === 401);
}

export function LegalReacceptGate({ children, onGateStateChange }: LegalReacceptGateProps) {
  const { status: authStatus, user, refresh, logout } = useAuth();
  const { t } = useT();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busy, setBusy] = useState<'accept' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [readerDoc, setReaderDoc] = useState<LegalDocId | null>(null);

  // Im SPA bleiben: Status einmal NACH Authed-Wechsel holen. Wenn der User
  // spaeter akzeptiert, holen wir bewusst neu — kein Auto-Refetch, sonst
  // wuerde der Server-Cache zwischen Klick und Modal-Update rennen.
  const fetchIdRef = useRef(0);
  const loadStatus = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setState({ kind: 'loading' });
    try {
      const next = await getLegalStatus();
      if (id !== fetchIdRef.current) return;
      setState({ kind: 'ready', status: next });
    } catch (error) {
      if (id !== fetchIdRef.current) return;
      if (isUnauthorizedError(error)) {
        setState({ kind: 'session_expired' });
      } else {
        setState({ kind: 'error' });
      }
    }
  }, []);

  useEffect(() => {
    if (authStatus !== 'authed') return;
    void loadStatus();
  }, [authStatus, loadStatus]);

  useEffect(() => {
    if (authStatus !== 'authed') return;
    return onSessionExpired(() => setState({ kind: 'session_expired' }));
  }, [authStatus]);

  // Der Server meldet 409 legal_acceptance_required, obwohl wir das Gate fuer
  // erledigt halten (neue Version mitten in der Sitzung, oder /legal-status
  // war beim Start fehlgeschlagen) → Status neu holen, damit der Dialog
  // erscheint. Nur aus 'clear'/'error' heraus und hoechstens alle 10 s: der
  // Bootstrap und offene Abfragen liefern viele 409er auf einmal, ein Neuladen
  // waehrend 'loading'/'blocking' liesse den Dialog flackern.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const lastLegalRefetchRef = useRef(0);
  useEffect(() => {
    if (authStatus !== 'authed') return;
    return onLegalAcceptanceRequired(() => {
      const current = stateRef.current;
      const believesClear = current.kind === 'error'
        || (current.kind === 'ready' && current.status.outdated.length === 0);
      if (!believesClear) return;
      const now = Date.now();
      if (now - lastLegalRefetchRef.current < LEGAL_REFETCH_MIN_INTERVAL_MS) return;
      lastLegalRefetchRef.current = now;
      void loadStatus();
    });
  }, [authStatus, loadStatus]);

  const handleAccept = useCallback(async () => {
    if (busy) return;
    setActionError(null);
    setBusy('accept');
    try {
      const next = await acceptPendingLegal();
      // Server hat erfolgreich geschrieben — eigenen Auth-Cache refreshen
      // (Akzept wurde in auth/me sichtbar), dann Gate-State neu setzen.
      await refresh().catch(() => undefined);
      setState({ kind: 'ready', status: next });
    } catch (error) {
      if (isUnauthorizedError(error)) {
        setState({ kind: 'session_expired' });
      } else {
        setActionError(t('legal_reaccept.accept_failed'));
      }
    } finally {
      setBusy(null);
    }
  }, [busy, refresh, t]);

  const handleLogout = useCallback(() => {
    void logout().catch(() => undefined);
  }, [logout]);

  const openReader = useCallback((doc: LegalDocument) => {
    setReaderDoc(LEGAL_SLUG_BY_DOCUMENT_KEY[doc.key]);
  }, []);

  const closeReader = useCallback(() => {
    setReaderDoc(null);
  }, []);

  const gateState: LegalReacceptGateState = authStatus !== 'authed' || state.kind === 'error'
    ? 'clear'
    : state.kind === 'loading'
      ? 'checking'
      : state.kind === 'session_expired'
        ? 'blocking'
        : state.status.outdated.length > 0
          ? 'blocking'
          : 'clear';

  useEffect(() => {
    onGateStateChange?.(gateState);
  }, [gateState, onGateStateChange]);

  // Vor Auth-Aufloesung oder im Ladezustand (wenn keine outdated-Info da ist):
  // einfach children rendern. Der Server verbietet ohnehin Aktionen ohne
  // gültige Auth, und das eigentliche Gate muss nur sichtbar sein, wenn
  // wirklich etwas zu akzeptieren ist.
  if (authStatus !== 'authed') return <>{children}</>;

  if (state.kind === 'loading') return <>{children}</>;

  if (state.kind === 'session_expired') {
    return (
      <>
        {children}
        <SessionExpiredOverlay onLogin={handleLogout} />
      </>
    );
  }

  if (state.kind === 'error') {
    // DA-Fix R6: Ein FEHLGESCHLAGENER /legal-status-Abruf (Netz/CORS/500) darf
    // die App NIEMALS blockieren — sonst sperrt ein transienter Fehler ALLE
    // User aus. Das Gate blockiert NUR bei tatsächlich veralteten Dokumenten;
    // bei einem Abruf-Fehler lassen wir die App normal laufen (fail-open) und
    // prüfen beim nächsten Mount/Session erneut.
    // ⚠️ DA-R2-Vorbehalt: Dies ist eine BEWUSSTE „Verfügbarkeit > Compliance"-
    // Wahl für den (seltenen, transienten) Abruf-Fehler-Fall. Es gibt aktuell
    // KEIN Server-Middleware-Gate, das mutierende/Payment-Routen bei veralteter
    // Zustimmung hart sperrt — ein offener Punkt, falls Compliance ein hartes
    // Backstop verlangt (dann zusätzlich serverseitig erzwingen).
    return <>{children}</>;
  }

  const { outdated } = state.status;

  if (outdated.length === 0) return <>{children}</>;

  return (
    <>
      {children}
      <BlockingGateOverlay
        docs={outdated}
        allowLogout={!(user?.is_guest || state.status.is_guest)}
        busy={busy}
        actionError={actionError}
        nestedDialogOpen={readerDoc !== null}
        onAccept={handleAccept}
        onLogout={handleLogout}
        onOpenDoc={openReader}
        labels={{
          headline: state.status.presentation?.headline ?? t('legal_reaccept.headline'),
          hint: state.status.presentation?.hint ?? t('legal_reaccept.accept_hint'),
          changesHeading: state.status.presentation?.changes_heading ?? t('legal_reaccept.changes.heading'),
          declineHint: user?.is_guest || state.status.is_guest
            ? state.status.presentation?.guest_decline_hint ?? t('legal_reaccept.guest_decline_hint')
            : state.status.presentation?.decline_hint ?? t('legal_reaccept.decline_hint'),
          lastUpdatedPrefix: state.status.presentation?.last_updated_prefix ?? t('legal_reaccept.last_updated_prefix'),
          accept: state.status.presentation?.accept ?? t('legal_reaccept.accept_btn'),
          // Review-Fix (74-misc, minor): der Knopf meldet tatsaechlich ab
          // (logout(), siehe handleLogout) -- "App schliessen" (close_app_btn)
          // suggerierte etwas anderes. Eigener, ehrlicher Schluessel; der
          // Server kennt close_app_btn weiterhin fuer alte Clients.
          logout: state.status.presentation?.logout ?? t('legal_reaccept.logout_btn'),
        }}
      />
      <LegalDialog doc={readerDoc} onClose={closeReader} layer="foreground" />
    </>
  );
}

interface BlockingGateOverlayProps {
  docs: LegalDocument[];
  allowLogout: boolean;
  busy: 'accept' | null;
  actionError: string | null;
  nestedDialogOpen: boolean;
  onAccept: () => void;
  onLogout: () => void;
  onOpenDoc: (doc: LegalDocument) => void;
  labels: {
    headline: string;
    changesHeading: string;
    hint: string;
    declineHint: string;
    lastUpdatedPrefix: string;
    accept: string;
    logout: string;
  };
}

function BlockingGateOverlay({
  docs,
  allowLogout,
  busy,
  actionError,
  nestedDialogOpen,
  onAccept,
  onLogout,
  onOpenDoc,
  labels,
}: BlockingGateOverlayProps) {
  const { t } = useT();

  // 74-misc Befund c: es gibt keinen "weichen" Zwischenzustand mehr (kein
  // Postpone, kein Countdown) — sobald outdated.length > 0, gilt IMMER dasselbe
  // ruhige Gate mit genau zwei Wegen (Zustimmen/Abmelden). Back/Escape/
  // Overlay-Click bleiben deshalb konsequent gesperrt (wie frueher nur im
  // Hard-Gate): useModalBackClose + closable=false in DialogBase reicht dafuer
  // nicht ganz, weil das native Bruecken-Register den Closer trotzdem
  // aufrufen wuerde — wir blocken die globalen Bruecken daher direkt.
  useEffect(() => {
    if (nestedDialogOpen || typeof window === 'undefined') return undefined;
    const bridge = window.LorathBackHandler;
    if (!bridge) return undefined;

    const blocker = () => true;
    if (bridge.push) {
      const cleanup = bridge.push(blocker);
      return typeof cleanup === 'function' ? cleanup : undefined;
    }
    if (bridge.register) {
      const cleanup = bridge.register(blocker);
      return typeof cleanup === 'function' ? cleanup : undefined;
    }
    if (bridge.add) {
      bridge.add(blocker);
      return () => bridge.remove?.(blocker);
    }
    return undefined;
  }, [nestedDialogOpen]);

  // Escape zusaetzlich abfangen (Web/PWA Fallback ohne Bridge).
  useEffect(() => {
    if (nestedDialogOpen || typeof document === 'undefined') return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [nestedDialogOpen]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`${styles.overlay} ${styles.hardOverlay}`}
      role="presentation"
      // Klicks auf den Hintergrund NICHT durchlassen — die zwei Wege
      // (Zustimmen/Abmelden) sind bewusst nur ueber die Knoepfe erreichbar.
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      data-legal-gate="active"
    >
      <div
        className={styles.panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="legal-reaccept-headline"
        aria-describedby="legal-reaccept-hint"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="legal-reaccept-headline" className={styles.headline}>
          {labels.headline}
        </h2>

        <p id="legal-reaccept-hint" className={styles.hint}>
          {labels.hint}
        </p>

        <h3 className={styles.docLabel}>{labels.changesHeading}</h3>
        <ul className={styles.docList}>
          {docs.map((doc) => (
            <li key={doc.key} className={styles.docItem}>
              <button
                type="button"
                className={styles.docLink}
                onClick={() => onOpenDoc(doc)}
              >
                <span className={styles.docLabel}>{doc.label}</span>
                <span className={styles.docVersion}>
                  {labels.lastUpdatedPrefix}: {doc.currentVersion}
                </span>
              </button>
              <p className={styles.hint}>{doc.summary ?? t(`legal_reaccept.changes.${doc.key}`)}</p>
            </li>
          ))}
        </ul>

        {/* Gäste behalten ihre Sitzung; ohne Zustimmung bleibt die Nutzung gesperrt. */}
        <p className={styles.hint}>{labels.declineHint}</p>

        {actionError ? (
          <p className={styles.error} role="status">
            {actionError}
          </p>
        ) : null}

        <div className={styles.actions}>
          <Button
            variant="primary"
            className={styles.confirmAction}
            onClick={onAccept}
            loading={busy === 'accept'}
            disabled={busy !== null}
          >
            {labels.accept}
          </Button>
          {allowLogout ? <Button
            variant="secondary"
            onClick={onLogout}
            disabled={busy !== null}
          >
            {labels.logout}
          </Button> : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface SessionExpiredOverlayProps {
  onLogin: () => void;
}

function SessionExpiredOverlay({ onLogin }: SessionExpiredOverlayProps) {
  const { t } = useT();
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={`${styles.overlay} ${styles.softOverlay}`} role="presentation">
      <div className={styles.panel} role="alertdialog" aria-modal="true" aria-live="assertive">
        <h2 className={styles.headline}>{t('legal_reaccept.session_expired')}</h2>
        <p className={styles.hint}>{t('legal_reaccept.session_expired_hint')}</p>
        <div className={styles.actions}>
          <Button variant="primary" fullWidth onClick={onLogin}>
            {t('legal_reaccept.login_again')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
